import Color from 'color';
import { noop } from 'lodash';
import Quill from 'quill';
import {
  memo,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useMemo,
} from 'react';
import styled, { css } from 'styled-components';
import { v4 as uuid } from 'uuid';

import { responsive } from '@hivebrite/design-system/Theme';

import {
  copyEmbedBlotWorkaround,
  getQuillDataTestId,
} from 'hb-react/shared/components/DesignSystem/QuillEditor/utils';
import {
  mailtoBindings,
  linkBindings,
} from 'hb-react/shared/components/DesignSystem/QuillEditor/utils/addLinkStyle';
import { listStyles } from 'hb-react/shared/constants/styles';

import { useQuillEditorContext } from '../contexts/QuillEditorContext';

import '../formats/register'; // Register all formats
import '../modules/register'; // Register all modules

// .ql-container is there to act as the quill "input" when trying to find the previous focusable element.
const focusableElements =
  '.ql-container, button, [href], input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

export const styles =
  ({
    convertDefaultLinkColorToHex = false,
    defaultLinkColor = true,
    disableLinks = true,
    isHeadline = false,
    isDefaultLinkColorImportant = true,
    underlineLinks = true,
    withListStyles = true,
  } = {}) =>
  (props) => {
    const linkColorSuffix = isDefaultLinkColorImportant ? '!important' : '';

    return css`
      h1,
      h2,
      h3,
      h4,
      h5,
      h6 {
        font-size: ${isHeadline && 'inherit !important'};
        font-weight: ${isHeadline && 'inherit !important'};
        line-height: ${isHeadline && 'inherit !important'};
        margin: ${isHeadline && '0 !important'};
      }

      p {
        margin: 0 0 ${props.theme.space[3]}px 0;
        padding: 0;

        &:last-child {
          margin-bottom: 0;
        }
      }

      a {
        ${defaultLinkColor &&
        `color: ${
          convertDefaultLinkColorToHex
            ? Color(props.theme.colors.link).hex()
            : props.theme.colors.link
        } ${linkColorSuffix}`};
        text-decoration: ${underlineLinks ? 'underline' : 'none'};

        ${disableLinks && 'pointer-events: none'};
      }
      .custom-variable-blot > span {
        border: 1px dashed #ccc;
        padding: 0px 2px;
      }

      ${withListStyles &&
      css`
        ul,
        ol {
          margin: 0 0 ${props.theme.space[3]}px 0;
          padding: 0;

          &:last-child {
            margin-bottom: 0;
          }
        }

        ${listStyles()}
      `}
    `;
  };

const Wrapper = memo(
  forwardRef(({ initialValue, id, ...otherProps }, ref) => (
    <WrapperStyled
      ref={ref}
      dangerouslySetInnerHTML={{
        __html: initialValue,
      }}
      {...otherProps}
    />
  )),
  // Re-render only if maxHeight change
  (prevProps, nextProps) => prevProps.maxHeight === nextProps.maxHeight,
);

const ContentEditable = forwardRef(
  (
    {
      ariaDescribedBy,
      ariaLabel,
      ariaLabelledBy,
      autoFocus,
      /** @deprecated: use toolbarOptions.indent instead */
      enableIndent,
      historyMaxStack = 100,
      quilleditor,
      id,
      initialValue,
      isDisabled,
      isRequired,
      maxHeight,
      onChange,
      padding,
      placeholder,
      styleProps = {
        disableLinks: true,
        underlineLinks: true,
      },
      mentionsBoxId,
      activeMentionOptionId,
      toolbarRef,
      ...otherProps
    },
    ref,
  ) => {
    const {
      options: { toolbar: toolbarOptions },
    } = useQuillEditorContext();

    const nodeRef = useRef();
    const quillRef = useRef();

    const {
      DEFAULTS: { bindings },
    } = Quill.import('modules/keyboard');

    // The new toolbarOptions.indent adds indenting buttons and allow indenting/outdenting paragraphs, lists and more
    // However, it is not compatible with the old indenting system (enabled through the "enableIdent" prop),
    // which is still used in some places (notably the Builder TextEditor)
    const indentKeyboardBindings = useMemo(
      () =>
        enableIndent
          ? {
              indent: {
                key: 'tab',
                format: ['list'],
                handler: (range, context) => {
                  if (
                    context.format.list === 'ordered' ||
                    context.format.list === 'bullet'
                  ) {
                    return false;
                  }

                  if (context.collapsed && context.offset !== 0) {
                    return true;
                  }

                  quillRef.current.format('indent', '+1', Quill.sources.USER);
                },
              },
            }
          : toolbarOptions?.indent
            ? {
                // Handler to prevent keyboard trap where users can tab out of the editor
                tab: {
                  key: 'tab',
                  handler: (range, _context) => {
                    const format = quillRef.current.getFormat(range.index);

                    // If we're currently on a list item, we return immediately and let the other handlers take care of it.
                    if (format.list === 'bullet' || format.list === 'ordered') {
                      return true;
                    }

                    const focusable = [
                      ...document.querySelectorAll(focusableElements),
                    ];

                    const index = focusable.indexOf(quillRef.current.container);

                    const nextIndex = Math.min(index + 1, focusable.length - 1);

                    const nextFocusableElement = focusable[nextIndex];

                    if (nextFocusableElement) {
                      nextFocusableElement.focus();

                      return false;
                    }

                    return true;
                  },
                },
                // Similar handler to the one above except for Shift+Tab to navigate backwards
                shiftTab: {
                  key: 'tab',
                  shiftKey: true,
                  handler: (range, _context) => {
                    const format = quillRef.current.getFormat(range.index);

                    if (format.list === 'bullet' || format.list === 'ordered') {
                      return true;
                    }

                    const focusable = [
                      ...document.querySelectorAll(focusableElements),
                    ];

                    const index = focusable.indexOf(quillRef.current.container);

                    const previousIndex = Math.max(index - 1, 0);

                    const previousFocusableElement = focusable[previousIndex];

                    if (previousFocusableElement) {
                      previousFocusableElement.focus();

                      return false;
                    }

                    return true;
                  },
                },
                hbIndent: {
                  key: 'tab',
                  handler: (_range, _context) => {
                    quillRef.current.format('indent', '+1', Quill.sources.USER);
                  },
                },
                hbOutdent: {
                  key: 'tab',
                  shiftKey: true,
                  handler: (_range, _context) => {
                    quillRef.current.format('indent', '-1', Quill.sources.USER);
                  },
                },
              }
            : { tab: false },
      [enableIndent, toolbarOptions?.indent],
    );

    useEffect(() => {
      const identifier = uuid();

      toolbarRef?.current?.setAttribute(
        'id',
        `${toolbarRef.current.id}-${identifier}`,
      );
      quillRef.current = new Quill(nodeRef.current, {
        placeholder,
        modules: {
          clipboard: {
            // For our custom BetterClipboard module
            attributes: [
              toolbarOptions?.links ? 'link' : null,
              toolbarOptions?.bold ? 'bold' : null,
              toolbarOptions?.italic ? 'italic' : null,
              toolbarOptions?.underline ? 'underline' : null,
              toolbarOptions?.list_ordered || toolbarOptions?.list_bullet
                ? 'list'
                : null,
              toolbarOptions?.header ? 'customLineHeight' : null,
              toolbarOptions?.header ? 'header' : null,
              toolbarOptions?.color ? 'color' : null,
              'id', // Allow HTML id attribute to be preserved
            ].filter(Boolean),
          },
          history: {
            maxStack: historyMaxStack,
          },
          imageResize: {
            // modules: ['Resize', 'DisplaySize'], // Individual modules can be disabled if need be
          },
          keyboard: {
            bindings: {
              // Disable some default bindings if the option is not enabled
              bold: {
                handler: toolbarOptions?.bold ? bindings.bold.handler : noop,
              },
              italic: {
                handler: toolbarOptions?.italic
                  ? bindings.italic.handler
                  : noop,
              },
              underline: {
                handler: toolbarOptions?.underline
                  ? bindings.underline.handler
                  : noop,
              },
              ...indentKeyboardBindings,
              ...mailtoBindings,
              ...linkBindings,
            },
          },
          toolbar: toolbarRef?.current?.id && `#${toolbarRef.current.id}`,
        },
      });

      quillRef.current.root?.setAttribute('role', 'textbox');
      quillRef.current.root?.setAttribute('aria-multiline', 'true');
      if (id) {
        quillRef.current.root?.setAttribute('name', id);
        quillRef.current.root?.setAttribute('id', id);
        quillRef.current.root?.setAttribute(
          'data-testid',
          getQuillDataTestId(id),
        );
      }
      if (isRequired) {
        quillRef.current.root?.setAttribute('aria-required', 'true');
      }
      if (ariaLabel) {
        quillRef.current.root?.setAttribute('aria-label', ariaLabel);
      }
      if (ariaLabelledBy) {
        quillRef.current.root?.setAttribute('aria-labelledby', ariaLabelledBy);
      }
      if (ariaDescribedBy) {
        quillRef.current.root?.setAttribute(
          'aria-describedby',
          ariaDescribedBy,
        );
      }

      if (onChange) {
        quillRef.current.on(Quill.events.EDITOR_CHANGE, onChange);

        return () => quillRef.current.off(Quill.events.EDITOR_CHANGE, onChange);
      }

      const { container } = quillRef.current;

      container.oncopy = ($event) =>
        copyEmbedBlotWorkaround(quillRef, $event, false);
      container.oncut = ($event) =>
        copyEmbedBlotWorkaround(quillRef, $event, true);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      placeholder,
      historyMaxStack,
      onChange,
      isRequired,
      ariaLabel,
      ariaDescribedBy,
    ]);

    useEffect(() => {
      quillRef.current?.enable(!isDisabled);
    }, [isDisabled]);

    useEffect(() => {
      if (autoFocus) {
        const selection = quillRef.current.getSelection();

        quillRef.current.focus();

        if (!selection) {
          quillRef.current.setSelection(quillRef.current.getLength(), 0, 'api');
        }
      }
    }, [autoFocus]);

    useEffect(() => {
      if (mentionsBoxId) {
        quillRef.current?.root?.setAttribute('aria-controls', mentionsBoxId);
      } else {
        quillRef.current?.root?.removeAttribute('aria-controls');
      }
    }, [mentionsBoxId]);

    useEffect(() => {
      if (activeMentionOptionId && mentionsBoxId) {
        quillRef.current?.root?.setAttribute(
          'aria-activedescendant',
          activeMentionOptionId,
        );
      } else {
        quillRef.current?.root?.removeAttribute('aria-activedescendant');
      }
    }, [activeMentionOptionId, mentionsBoxId]);

    useImperativeHandle(ref, () => ({
      getQuillRef: () => quillRef.current,
      getHtml: () => quillRef.current?.root?.innerHTML || '',
    }));

    return (
      <Wrapper
        initialValue={initialValue}
        maxHeight={maxHeight}
        padding={padding}
        ref={nodeRef}
        styleProps={styleProps}
        {...otherProps}
      />
    );
  },
);

export default ContentEditable;

const WrapperStyled = styled.div`
  color: inherit;
  font-size: inherit;
  height: 100%;
  line-height: unset;
  padding: 0;
  user-select: auto;
  width: 100%;
  max-height: ${(props) => props.maxHeight}px;

  .ql-clipboard {
    height: 1px;
    left: -100000px;
    overflow-y: hidden;
    position: absolute;
    top: 50%;

    p {
      margin: 0;
      padding: 0;
    }
  }

  .ql-editor {
    height: 100%;
    outline: none;
    // On iOS 'auto' may prevent scrolling in some cases
    overflow-y: ${(props) => (props.maxHeight ? 'auto' : 'visible')};
    padding: ${(props) =>
      props.theme.space[props.padding] || props.padding || 0}px;
    text-align: inherit;
    white-space: pre-wrap;
    width: 100%;
    word-wrap: break-word;
    tab-size: 8;
    // To avoid zoom on iOS mobile devices, inputs must have font-size set to 16px on s & m
    font-size: ${({ theme }) => theme.fontSizes[3]} !important;
    ${responsive('l')`
      font-size: ${({ theme }) => theme.fontSizes[2]} !important;
    `}

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      margin-top: 0;
      margin-bottom: 0.5rem;
      font-family: inherit;
      font-weight: 500;
      line-height: 1.2;
      color: inherit;
    }

    h1 {
      font-size: 2.5rem;
    }

    h2 {
      font-size: 2rem;
    }

    h3 {
      font-size: 1.75rem;
    }

    h4 {
      font-size: 1.5rem;
    }

    h5 {
      font-size: 1.25rem;
    }

    h6 {
      font-size: 1rem;
    }

    .ql-align-center {
      text-align: center;
    }

    > * {
      cursor: text;
    }

    &.ql-blank:before {
      color: ${(props) => props.theme.input.placeholder};
      content: attr(data-placeholder);
      font-style: normal;
      left: ${(props) =>
        props.theme.space[props.padding] || props.padding || 0}px;
      line-height: inherit;
      overflow: hidden;
      pointer-events: none;
      position: absolute;
      right: ${(props) =>
        props.theme.space[props.padding] || props.padding || 0}px;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    ${(props) => styles(props.styleProps)(props)}
  }
`;
